/**
 * KB adapter — wraps the `/api/v1/kb/{kbId}/...` surface so AuiFiles
 * can browse a knowledge base with the same component used for
 * conversations and skills.
 *
 * AuiFiles consumes a flat file list and synthesises folders from
 * paths. The KB API paginates direct children; this adapter fetches a
 * single recursive page (`recursive=true`) and flattens file rows into
 * `VfsEntry` shape. Folder rows are dropped for now — when AuiFiles
 * learns to render real folder nodes the adapter can stop filtering
 * them and the `folders` capability flips to true. Same for `search`
 * (`useSearch` is wired but AuiFiles doesn't render the tab yet).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { kbApi } from '@/services/api';
import { usePlatformEvents } from '@/contexts/PlatformEventContext';
import type { PlatformEvent } from '@/types/events';
import type { VfsEntry } from '@/types/vfs';
import type { IndexStatus, VfsNode } from '@/types/kb';
import type {
  FilesBackend,
  FilesEntry,
  UseDeleteResult,
  UseEntriesResult,
  UseFoldersResult,
  UseSearchResult,
  UseUploadResult,
} from './types';
import type { VfsUploadJob } from '@/hooks/useVfsUpload';
import {
  addVirtualFolder,
  getVirtualFolders,
  pruneRealisedFolders,
  subscribeVirtualFolders,
} from './virtual-folders';

// Exported so the composer's `!`-mention candidate hook
// (`useMentionFileCandidates`) can drive its own `useQuery` off the exact
// same key + queryFn, sharing this flat-tree cache instead of re-walking.
export const KB_ENTRIES_KEY = (kbId: string) => ['kb', kbId, 'entries-flat'] as const;

/**
 * BFS-walk the entire KB tree. The backend's `?recursive=1` flag
 * doesn't actually walk INTO folders — it returns root children plus
 * folder rows but never files nested inside them. Walk ourselves: list
 * root, queue each folder we find, repeat. Cost = one HTTP call per
 * folder.
 */
export async function walkKbTree(kbId: string): Promise<{ items: VfsNode[] }> {
  const visited = new Set<string>();
  const out: VfsNode[] = [];
  const queue: string[] = [''];
  while (queue.length > 0) {
    const path = queue.shift() as string;
    if (visited.has(path)) continue;
    visited.add(path);
    const page = await kbApi.listNodes(kbId, {
      path: path || undefined,
      limit: 500,
    });
    for (const node of page.items) {
      out.push(node);
      if (node.kind === 'folder' && !visited.has(node.path)) {
        queue.push(node.path);
      }
    }
  }
  return { items: out };
}

function nodeToEntry(node: VfsNode, kbId: string): FilesEntry {
  return {
    fileId: node.fileId ?? node.path,
    ownerId: kbId,
    path: node.path,
    name: node.name,
    size: node.sizeBytes ?? 0,
    contentType: node.contentType ?? 'application/octet-stream',
    createdAt: node.createdAt,
    updatedAt: node.updatedAt,
    createdBy: 'user',
    kind: node.kind,
    indexStatus: node.indexStatus,
    derivatives: node.derivatives,
  };
}

function joinPath(prefix: string | undefined, name: string): string {
  const seg = name.trim() || 'untitled';
  if (!prefix) return seg;
  const trimmed = prefix.replace(/^\/+|\/+$/g, '');
  return trimmed ? `${trimmed}/${seg}` : seg;
}

// ─── Live cache reconciliation ──────────────────────────────────────────
//
// Mirrors `useVfsFiles` (src/hooks/useVfsFiles.ts): cold-load once via the
// `useEntries` query, then patch the cached tree IN PLACE from each WS event's
// payload — never a per-event refetch (that races the eventually-consistent
// `listNodes` read). The only refetch beyond the mount cold-load is on WS
// reconnect, to catch events missed while disconnected.
//
// KB shares one react-query cache (`KB_ENTRIES_KEY`) across `useEntries`,
// `useFolders`, and `useMentionFileCandidates`, so we apply the same
// upsert/move/delete merge semantics `useVfsFiles` applies to its local Map
// directly to that cache via `setQueryData`. Patches are in `VfsNode` space —
// the shape `walkKbTree` produces and every consumer expects.

type KbTreeData = { items: VfsNode[] };

function basename(path: string): string {
  return path.split('/').pop() || path;
}

/** Build a file `VfsNode` from a `kb.vfs.file.*` / `vfs.file.*` payload. KB
 *  events carry only `{ path, fileId, contentType, … }` — name is derived,
 *  size defaults to 0, and timestamps fall back to the envelope timestamp. */
function fileNodeFromEvent(
  kbId: string,
  d: Record<string, unknown>,
  ts: string,
): VfsNode {
  const path = (d.path ?? d.toPath ?? d.newPath) as string;
  return {
    kbId,
    path,
    name: (d.name as string | undefined) ?? basename(path),
    kind: 'file',
    fileId: d.fileId as string | undefined,
    sizeBytes: (d.sizeBytes as number | undefined) ?? (d.size as number | undefined) ?? 0,
    contentType: (d.contentType as string | undefined) ?? 'application/octet-stream',
    docType: d.docType as string | undefined,
    indexStatus: (d.indexStatus as IndexStatus | undefined) ?? 'pending',
    createdAt: (d.createdAt as string | undefined) ?? ts,
    updatedAt: (d.updatedAt as string | undefined) ?? ts,
  };
}

/** Merge an incoming row onto an existing one: prefer the event's
 *  path/name/updatedAt (it represents the change) but keep authoritative
 *  fields the event omits (real size, createdAt, an already-`indexed`
 *  status). Returns the SAME reference when nothing changed so react-query's
 *  structural sharing skips the re-render. */
function mergeNode(existing: VfsNode, incoming: VfsNode): VfsNode {
  const merged: VfsNode = {
    ...existing,
    path: incoming.path,
    name: incoming.name,
    updatedAt: incoming.updatedAt || existing.updatedAt,
    contentType: incoming.contentType || existing.contentType,
    sizeBytes: incoming.sizeBytes || existing.sizeBytes,
    createdAt: existing.createdAt || incoming.createdAt,
    indexStatus:
      incoming.indexStatus && incoming.indexStatus !== 'pending'
        ? incoming.indexStatus
        : existing.indexStatus ?? incoming.indexStatus,
  };
  if (
    merged.path === existing.path &&
    merged.name === existing.name &&
    merged.updatedAt === existing.updatedAt &&
    merged.contentType === existing.contentType &&
    merged.sizeBytes === existing.sizeBytes &&
    merged.indexStatus === existing.indexStatus
  ) {
    return existing;
  }
  return merged;
}

/** Upsert a node, deduped by `fileId` (files) or `path` (folders). */
function upsertNode(items: VfsNode[], node: VfsNode): VfsNode[] {
  const idx = items.findIndex((n) =>
    node.kind === 'file'
      ? n.kind === 'file' && !!node.fileId && n.fileId === node.fileId
      : n.kind === 'folder' && n.path === node.path,
  );
  if (idx === -1) return [...items, node];
  const merged = mergeNode(items[idx], node);
  if (merged === items[idx]) return items;
  const next = items.slice();
  next[idx] = merged;
  return next;
}

/** Drop a file row by fileId (fallback path). */
function removeFile(items: VfsNode[], fileId?: string, path?: string): VfsNode[] {
  const next = items.filter(
    (n) =>
      !(
        n.kind === 'file' &&
        ((fileId && n.fileId === fileId) || (path && n.path === path))
      ),
  );
  return next.length === items.length ? items : next;
}

/** Drop a folder and everything beneath it (cascade). */
function removeFolderCascade(items: VfsNode[], path: string): VfsNode[] {
  const prefix = `${path}/`;
  const next = items.filter((n) => !(n.path === path || n.path.startsWith(prefix)));
  return next.length === items.length ? items : next;
}

/** Re-prefix a folder and all descendants on `kb.vfs.folder.moved`. */
function renameFolderPrefix(items: VfsNode[], from: string, to: string): VfsNode[] {
  const prefix = `${from}/`;
  let changed = false;
  const next = items.map((n) => {
    if (n.path === from) {
      changed = true;
      return { ...n, path: to, name: basename(to) };
    }
    if (n.path.startsWith(prefix)) {
      changed = true;
      return { ...n, path: `${to}${n.path.slice(from.length)}` };
    }
    return n;
  });
  return changed ? next : items;
}

/**
 * Apply one platform event to the flat KB tree. Pure and idempotent —
 * returns the SAME `items` reference when the event changes nothing (so
 * react-query's structural sharing skips the re-render). Owner filtering is
 * the caller's job; this assumes the event belongs to `kbId`.
 */
export function reduceKbTree(
  items: VfsNode[],
  event: PlatformEvent,
  kbId: string,
): VfsNode[] {
  const d = (event.data ?? {}) as Record<string, unknown>;
  const ts = event.metadata?.timestamp ?? '';

  switch (event.eventType) {
    case 'vfs.file.created':
    case 'kb.vfs.file.created': {
      const node = fileNodeFromEvent(kbId, d, ts);
      if (!node.path) return items;
      // A stale/duplicate create must not undo a prior move: if the file
      // already sits at a different path, keep it there and only backfill.
      const existing = items.find(
        (n) => n.kind === 'file' && !!node.fileId && n.fileId === node.fileId,
      );
      if (existing && existing.path !== node.path) {
        node.path = existing.path;
        node.name = existing.name;
      }
      return upsertNode(items, node);
    }
    case 'vfs.file.updated':
    case 'kb.vfs.file.updated': {
      const node = fileNodeFromEvent(kbId, d, ts);
      if (!node.path) return items;
      return upsertNode(items, node);
    }
    case 'vfs.file.moved':
    case 'kb.vfs.file.moved': {
      const node = fileNodeFromEvent(kbId, d, ts); // path = toPath
      if (!node.path) return items;
      const from = (d.fromPath ?? d.oldPath) as string | undefined;
      let idx = node.fileId
        ? items.findIndex((n) => n.kind === 'file' && n.fileId === node.fileId)
        : -1;
      if (idx === -1 && from) {
        idx = items.findIndex((n) => n.kind === 'file' && n.path === from);
      }
      if (idx === -1) return upsertNode(items, node);
      const merged = mergeNode(items[idx], node);
      if (merged === items[idx]) return items;
      const next = items.slice();
      next[idx] = merged;
      return next;
    }
    case 'vfs.file.deleted':
    case 'kb.vfs.file.deleted': {
      const fileId = d.fileId as string | undefined;
      const path = d.path as string | undefined;
      if (!fileId && !path) return items;
      return removeFile(items, fileId, path);
    }
    case 'kb.vfs.folder.created': {
      const path = d.path as string | undefined;
      if (!path) return items;
      return upsertNode(items, {
        kbId,
        path,
        name: basename(path),
        kind: 'folder',
        createdAt: ts,
        updatedAt: ts,
      });
    }
    case 'kb.vfs.folder.moved': {
      const from = (d.fromPath ?? d.oldPath ?? d.path) as string | undefined;
      const to = (d.toPath ?? d.newPath) as string | undefined;
      if (!from || !to) return items;
      return renameFolderPrefix(items, from, to);
    }
    case 'kb.vfs.folder.deleted': {
      const path = d.path as string | undefined;
      if (!path) return items;
      return removeFolderCascade(items, path);
    }
    case 'kb.search-content.added': {
      // A previously-pending file finished indexing. Payload keys the file
      // as `docId` (no path); our file rows use it as `fileId`.
      const id = (d.fileId ?? d.docId) as string | undefined;
      if (!id) return items;
      const idx = items.findIndex((n) => n.kind === 'file' && n.fileId === id);
      if (idx === -1 || items[idx].indexStatus === 'indexed') return items;
      const next = items.slice();
      next[idx] = { ...items[idx], indexStatus: 'indexed' };
      return next;
    }
    default:
      return items;
  }
}

/** Does this event realise a (possibly virtual) folder — i.e. a file landed
 *  somewhere, so any placeholder folder it now lives in should be pruned. */
function prunablePathForEvent(event: PlatformEvent): string | undefined {
  const t = event.eventType;
  if (
    t === 'vfs.file.created' ||
    t === 'kb.vfs.file.created' ||
    t === 'vfs.file.moved' ||
    t === 'kb.vfs.file.moved'
  ) {
    const d = (event.data ?? {}) as Record<string, unknown>;
    return (d.path ?? d.toPath ?? d.newPath) as string | undefined;
  }
  return undefined;
}

/**
 * Live cache reconciler for one open KB. Replaces the old invalidate-on-every-
 * event hook. Wired into `useEntries`.
 */
function useKbCacheReconciler(kbId: string): void {
  const queryClient = useQueryClient();
  const { subscribe, isConnected } = usePlatformEvents();

  // Refetch on WS reconnect — catches events missed during a disconnect.
  // (Same pattern as useVfsFiles.) The mount cold-load is the query itself.
  const wasConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = wasConnectedRef.current;
    wasConnectedRef.current = isConnected;
    if (!wasConnected && isConnected) {
      queryClient.invalidateQueries({ queryKey: KB_ENTRIES_KEY(kbId) });
    }
  }, [isConnected, kbId, queryClient]);

  useEffect(() => {
    const handler = (event: PlatformEvent) => {
      const d = (event.data ?? {}) as Record<string, unknown>;
      // Owner guard: `vfs.*` envelopes carry `subjectId: kbId`; KB-specific
      // events embed `kbId` in `data`.
      const ownerMatch =
        d.kbId === kbId || d.ownerId === kbId || event.subjectId === kbId;
      if (!ownerMatch) return;

      queryClient.setQueryData<KbTreeData>(KB_ENTRIES_KEY(kbId), (old) => {
        if (!old) return old;
        const items = reduceKbTree(old.items, event, kbId);
        return items === old.items ? old : { ...old, items };
      });

      // A file landing inside a folder realises any virtual placeholder for
      // that folder — drop it so it doesn't shadow the real row.
      const prunePath = prunablePathForEvent(event);
      if (prunePath) pruneRealisedFolders(kbId, prunePath);
    };

    const unsub = subscribe(
      [
        { eventType: 'vfs.file.created', subjectId: kbId },
        { eventType: 'vfs.file.updated', subjectId: kbId },
        { eventType: 'vfs.file.deleted', subjectId: kbId },
        { eventType: 'vfs.file.moved', subjectId: kbId },
        { eventType: 'kb.vfs.file.created' },
        { eventType: 'kb.vfs.file.updated' },
        { eventType: 'kb.vfs.file.deleted' },
        { eventType: 'kb.vfs.file.moved' },
        { eventType: 'kb.vfs.folder.created' },
        { eventType: 'kb.vfs.folder.updated' },
        { eventType: 'kb.vfs.folder.moved' },
        { eventType: 'kb.vfs.folder.deleted' },
        { eventType: 'kb.search-content.added' },
      ],
      handler,
    );
    return unsub;
  }, [kbId, subscribe, queryClient]);
}

export function createKbBackend(kbId: string): FilesBackend {
  return {
    kind: 'kb',
    ownerId: kbId,
    capabilities: {
      // Real folders persist server-side; AuiFiles already shows them
      // in the tree (via `useFolders` alongside file-derived ancestors).
      // Search is gated on the AuiFiles search affordance — flipping
      // this to true makes the header's Search icon appear and routes
      // `backend.useSearch` calls through `kbApi.search`.
      folders: true,
      indexStatus: false,
      search: true,
      copy: true,
      // KB loads folder children on demand via `listChildren` — a KB can hold
      // thousands of files and a single folder thousands of children, so the
      // host drives a sparse per-folder store + pagination instead of walking
      // the whole tree up front.
      lazyChildren: true,
      // Flat list view needs the backend's `recursive=true` to return every
      // descendant file paginated. That fix isn't live yet (today it returns
      // folder rows but not nested files — see `walkKbTree`), so keep the
      // list-view toggle hidden until the backend ships it, then flip to true.
      recursiveList: false,
    },

    async listChildren(path, opts) {
      // Direct children of `path` (recursive omitted) or — once the backend
      // supports it — a flat page of every descendant file (`recursive: true`).
      // The server returns rows already in the deterministic order the host
      // relies on (see `./order.ts`); we never re-sort paginated pages.
      const page = await kbApi.listNodes(kbId, {
        path: path || undefined,
        limit: opts?.limit ?? 100,
        cursor: opts?.cursor,
        recursive: opts?.recursive,
      });
      const children = page.items
        // Flat list view is files-only; tree view keeps folder rows.
        .filter((n) => (opts?.recursive ? n.kind === 'file' : true))
        .map((n) => ({
          kind: n.kind,
          path: n.path,
          name: n.name,
          entry: n.kind === 'file' ? nodeToEntry(n, kbId) : undefined,
        }));
      return {
        children,
        nextCursor: page.nextToken ?? null,
        hasMore: !!page.nextToken,
        totalCount: page.totalCount,
      };
    },

    async statEntry(path) {
      // Resolve a single path directly (search-result open for a folder the
      // user never expanded). A missing / non-file / deleted path 404s —
      // report that as `null` so the caller can surface a friendly message.
      try {
        const node = await kbApi.statNode(kbId, path);
        return node.kind === 'file' ? nodeToEntry(node, kbId) : null;
      } catch {
        return null;
      }
    },

    useEntries(): UseEntriesResult {
      // KB is a lazy backend: AuiFiles sources the tree per-folder via
      // `listChildren`, so `useEntries` no longer BFS-walks the whole tree
      // (that was the over-fetch this change removes). It exists only to keep
      // the `!`-mention cache (`KB_ENTRIES_KEY`, populated on demand by
      // `useMentionFileCandidates`) reconciled while a KB is being browsed.
      useKbCacheReconciler(kbId);
      return {
        entries: [],
        isLoading: false,
        error: null,
        refetch: async () => {},
      };
    },

    useOnEvent(handler) {
      // Normalise KB platform events into the wire-agnostic `FilesEvent`
      // union so AuiFiles can reconcile its lazy per-folder store. (The
      // mention cache is reconciled separately in `useEntries`.)
      const { subscribe } = usePlatformEvents();
      const handlerRef = useRef(handler);
      handlerRef.current = handler;
      useEffect(() => {
        const unsub = subscribe(
          [
            { eventType: 'vfs.file.created', subjectId: kbId },
            { eventType: 'vfs.file.updated', subjectId: kbId },
            { eventType: 'vfs.file.deleted', subjectId: kbId },
            { eventType: 'vfs.file.moved', subjectId: kbId },
            { eventType: 'kb.vfs.file.created' },
            { eventType: 'kb.vfs.file.updated' },
            { eventType: 'kb.vfs.file.deleted' },
            { eventType: 'kb.vfs.file.moved' },
            { eventType: 'kb.vfs.folder.created' },
            { eventType: 'kb.vfs.folder.moved' },
            { eventType: 'kb.vfs.folder.deleted' },
            { eventType: 'kb.search-content.added' },
          ],
          (event: PlatformEvent) => {
            const fire = handlerRef.current;
            const d = (event.data ?? {}) as Record<string, unknown>;
            const ownerMatch =
              d.kbId === kbId || d.ownerId === kbId || event.subjectId === kbId;
            if (!ownerMatch) return;
            const ts = event.metadata?.timestamp ?? '';
            switch (event.eventType) {
              case 'vfs.file.created':
              case 'kb.vfs.file.created': {
                const node = fileNodeFromEvent(kbId, d, ts);
                if (!node.path) return;
                fire({ type: 'file-created', entry: nodeToEntry(node, kbId) });
                return;
              }
              case 'vfs.file.updated':
              case 'kb.vfs.file.updated': {
                const node = fileNodeFromEvent(kbId, d, ts);
                if (!node.path) return;
                fire({ type: 'file-updated', entry: nodeToEntry(node, kbId) });
                return;
              }
              case 'vfs.file.moved':
              case 'kb.vfs.file.moved': {
                const node = fileNodeFromEvent(kbId, d, ts);
                if (!node.path) return;
                const from = (d.fromPath ?? d.oldPath) as string | undefined;
                fire({
                  type: 'file-moved',
                  fileId: node.fileId ?? '',
                  from: from ?? '',
                  to: node.path,
                  entry: nodeToEntry(node, kbId),
                });
                return;
              }
              case 'vfs.file.deleted':
              case 'kb.vfs.file.deleted': {
                const fileId = d.fileId as string | undefined;
                const path = d.path as string | undefined;
                if (!fileId && !path) return;
                fire({ type: 'file-deleted', fileId: fileId ?? '', path: path ?? '' });
                return;
              }
              case 'kb.vfs.folder.created': {
                const path = d.path as string | undefined;
                if (path) fire({ type: 'folder-created', path });
                return;
              }
              case 'kb.vfs.folder.moved': {
                const from = (d.fromPath ?? d.oldPath ?? d.path) as string | undefined;
                const to = (d.toPath ?? d.newPath) as string | undefined;
                if (from && to) fire({ type: 'folder-moved', from, to });
                return;
              }
              case 'kb.vfs.folder.deleted': {
                const path = d.path as string | undefined;
                if (path) fire({ type: 'folder-deleted', path });
                return;
              }
              case 'kb.search-content.added': {
                const id = (d.fileId ?? d.docId) as string | undefined;
                if (id) fire({ type: 'file-indexed', fileId: id, path: (d.path as string) ?? '' });
                return;
              }
            }
          },
        );
        return unsub;
      }, [subscribe]);
    },

    useUpload(): UseUploadResult {
      const queryClient = useQueryClient();
      const [jobs, setJobs] = useState<VfsUploadJob[]>([]);
      const abortControllers = useRef<Map<string, AbortController>>(new Map());

      const updateJob = useCallback((path: string, patch: Partial<VfsUploadJob>) => {
        setJobs((prev) => prev.map((j) => (j.path === path ? { ...j, ...patch } : j)));
      }, []);

      const uploadFile = useCallback(
        async (
          file: File,
          opts?: { prefix?: string; name?: string },
        ): Promise<VfsEntry | null> => {
          const path = joinPath(opts?.prefix, opts?.name ?? file.name);
          setJobs((prev) => {
            const without = prev.filter((j) => j.path !== path);
            return [...without, { file, path, progress: 0, status: 'pending' }];
          });

          const controller = new AbortController();
          abortControllers.current.set(path, controller);
          try {
            updateJob(path, { status: 'uploading' });
            const init = await kbApi.initUpload(kbId, {
              path,
              contentType: file.type || 'application/octet-stream',
            });
            if (!init.uploadUrl) throw new Error('Upload URL missing');

            // KB presigned PUT doesn't yet stream upload progress via
            // XHR; fetch() is good enough for the v1 surface.
            const put = await fetch(init.uploadUrl, {
              method: 'PUT',
              body: file,
              signal: controller.signal,
              headers: {
                'Content-Type': file.type || 'application/octet-stream',
                'x-amz-server-side-encryption': 'AES256',
                ...(init.uploadHeaders ?? {}),
              },
            });
            if (!put.ok) throw new Error(`Upload failed (${put.status})`);

            updateJob(path, { status: 'completing', progress: 1 });
            const node = await kbApi.completeUpload(kbId, {
              path,
              fileId: init.fileId,
              contentType: file.type || undefined,
              sizeBytes: file.size,
            });
            const entry = nodeToEntry(node, kbId);
            updateJob(path, { status: 'done', result: entry });
            // Drop any virtual folder this file now lives inside —
            // path is realised on the server, the placeholder would
            // only shadow the real folder row coming back from /nodes.
            pruneRealisedFolders(kbId, entry.path);
            // The server emits `vfs.file.*` events for the KB owner but
            // we invalidate the flat cache too so the new row is visible
            // even before the WS round-trip completes.
            queryClient.invalidateQueries({ queryKey: KB_ENTRIES_KEY(kbId) });
            return entry;
          } catch (err) {
            const message =
              err instanceof Error ? err.message : String(err);
            updateJob(path, { status: 'error', error: message });
            return null;
          } finally {
            abortControllers.current.delete(path);
          }
        },
        [queryClient, updateJob],
      );

      const cancel = useCallback((path: string) => {
        abortControllers.current.get(path)?.abort();
      }, []);

      const clearDone = useCallback(() => {
        setJobs((prev) => prev.filter((j) => j.status !== 'done'));
      }, []);

      return { jobs, uploadFile, cancel, clearDone };
    },

    useFolders(): UseFoldersResult {
      // Real folders now arrive as folder rows from `listChildren`, so this
      // hook no longer walks the tree. It tracks only client-side virtual
      // folders (a "create folder then upload into it" flow before the
      // server round-trip) and exposes `mkdir`.
      const [virtualFolderPaths, setVirtualFolderPaths] = useState<string[]>(
        () => Array.from(getVirtualFolders(kbId)),
      );
      useEffect(() => {
        const unsub = subscribeVirtualFolders(kbId, () => {
          setVirtualFolderPaths(Array.from(getVirtualFolders(kbId)));
        });
        return unsub;
      }, []);

      const folders = virtualFolderPaths;

      const m = useMutation({
        mutationFn: (input: { path: string }) => kbApi.mkdir(kbId, input),
        onSuccess: (_node, vars) => {
          // Immediate visibility via the virtual set; the server emits
          // `kb.vfs.folder.created`, which AuiFiles' store reconciler then
          // folds into the lazy tree.
          addVirtualFolder(kbId, vars.path);
        },
      });

      const mkdir = useCallback(
        async ({ path }: { path: string }) => {
          await m.mutateAsync({ path });
        },
        [m],
      );

      return { folders, mkdir, isCreating: m.isPending };
    },

    useDelete(): UseDeleteResult {
      const queryClient = useQueryClient();
      const [isDeleting, setIsDeleting] = useState(false);
      const [error, setError] = useState<Error | null>(null);

      const deleteFile = useCallback(
        async (path: string): Promise<boolean> => {
          setIsDeleting(true);
          setError(null);
          try {
            await kbApi.removeNode(kbId, path);
            queryClient.invalidateQueries({ queryKey: KB_ENTRIES_KEY(kbId) });
            return true;
          } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            return false;
          } finally {
            setIsDeleting(false);
          }
        },
        [queryClient],
      );

      // KB has no separate delete-folder endpoint — removeNode of a
      // folder path cascades on the backend. Returns null for the count
      // since the API doesn't report it.
      const deleteFolder = useCallback(
        async (prefix: string): Promise<number | null> => {
          setIsDeleting(true);
          setError(null);
          try {
            await kbApi.removeNode(kbId, prefix);
            queryClient.invalidateQueries({ queryKey: KB_ENTRIES_KEY(kbId) });
            return null;
          } catch (err) {
            setError(err instanceof Error ? err : new Error(String(err)));
            return null;
          } finally {
            setIsDeleting(false);
          }
        },
        [queryClient],
      );

      return { deleteFile, deleteFolder, isDeleting, error };
    },

    async move({ from, to, overwrite }) {
      const node = await kbApi.move(kbId, { from, to, overwrite });
      return nodeToEntry(node, kbId);
    },

    async copy({ from, to, overwrite }) {
      const node = await kbApi.copy(kbId, { from, to, overwrite });
      return nodeToEntry(node, kbId);
    },

    async getDownloadUrl(path, derivative) {
      return kbApi.downloadUrl(kbId, path, derivative);
    },

    async readText(path, _version, derivative) {
      // KB content endpoint streams the canonical fileId-resolved body
      // each time, so we don't need to forward a cache-buster version.
      return kbApi.readText(kbId, path, derivative);
    },

    useSearch(): UseSearchResult {
      const m = useMutation({
        mutationFn: (input: { query: string; topK?: number; includeText?: boolean }) =>
          kbApi.search(kbId, input),
      });
      return {
        data: m.data
          ? {
              query: m.data.query,
              results: m.data.results.map((r) => ({
                fileId: r.fileId,
                path: r.path,
                name: r.name,
                score: r.score,
                chunkId: r.chunkId,
                indexStatus: r.indexStatus,
                text: r.text,
              })),
            }
          : undefined,
        isPending: m.isPending,
        isSuccess: m.isSuccess,
        error: m.error ?? null,
        mutateAsync: async (input) => {
          const res = await m.mutateAsync(input);
          return {
            results: res.results.map((r) => ({
              fileId: r.fileId,
              path: r.path,
              name: r.name,
              score: r.score,
              chunkId: r.chunkId,
              indexStatus: r.indexStatus,
              text: r.text,
            })),
          };
        },
      };
    },
  };
}
